import React from 'react';
import PropTypes from 'prop-types';
import { selectSystemProps, useScrollBlocking, useThemeTokens, getTokensPropType } from '@telus-uds/components-base';
import { Portal } from '@gorhom/portal';
import styled from 'styled-components';
import SpinnerContent from './SpinnerContent';
import { htmlAttrs, media } from '../utils';
import { BACKDROP_OPACITY, BACKDROP_Z_INDEX } from './constants';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([htmlAttrs]);
const SpinnerContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Spinner__SpinnerContainer",
  componentId: "components-web__sc-116rqck-0"
})(_ref => {
  let {
    inline,
    fullScreen,
    overlay
  } = _ref;
  return {
    position: 'relative',
    ...(inline && {
      display: overlay ? 'inline-block' : 'block',
      ...media().from('md').css({
        display: 'inline-block'
      })
    }),
    ...(fullScreen && {
      top: '50%',
      transform: 'translateY(-50%)'
    })
  };
});
const ContentOverlay = /*#__PURE__*/styled.div.withConfig({
  displayName: "Spinner__ContentOverlay",
  componentId: "components-web__sc-116rqck-1"
})({
  position: 'absolute',
  width: '100%',
  height: '100%',
  zIndex: BACKDROP_Z_INDEX
});
const FullscreenOverlay = /*#__PURE__*/styled.div.withConfig({
  displayName: "Spinner__FullscreenOverlay",
  componentId: "components-web__sc-116rqck-2"
})(_ref2 => {
  let {
    fullScreenOverLayBackground
  } = _ref2;
  return {
    position: 'fixed',
    width: '100vw',
    height: '100vh',
    top: 0,
    left: 0,
    zIndex: BACKDROP_Z_INDEX,
    backgroundColor: fullScreenOverLayBackground
  };
});
const OpaqueContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Spinner__OpaqueContainer",
  componentId: "components-web__sc-116rqck-3"
})({
  opacity: BACKDROP_OPACITY
});
const recursiveMap = (children, fn) => React.Children.map(children, child => {
  if (! /*#__PURE__*/React.isValidElement(child)) {
    return child;
  }
  if (child.props.children) {
    return fn(/*#__PURE__*/React.cloneElement(child, {
      children: recursiveMap(child.props.children, fn)
    }));
  }
  return fn(child);
});

/**
 * Loading indicator.
 */
const Spinner = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    children,
    fullScreen = false,
    inline = false,
    label,
    labelPosition,
    show = false,
    isStatic = false,
    tokens,
    variant = {},
    ...rest
  } = _ref3;
  const {
    fullScreenOverLayBackground,
    size,
    thickness
  } = useThemeTokens('Spinner', tokens, variant);
  const {
    size: sizeVariant = 'large'
  } = variant;
  useScrollBlocking([fullScreen, show]);
  if (!show) {
    return children ?? null;
  }

  // Full screen spinner
  if (fullScreen) {
    return /*#__PURE__*/_jsx(Portal, {
      children: /*#__PURE__*/_jsx(FullscreenOverlay, {
        fullScreenOverLayBackground: fullScreenOverLayBackground,
        children: /*#__PURE__*/_jsx(SpinnerContainer, {
          inline: inline,
          fullScreen: fullScreen,
          "aria-live": "assertive",
          ...selectProps(rest),
          children: /*#__PURE__*/_jsx(SpinnerContent, {
            label: label,
            labelPosition: labelPosition,
            overlay: true,
            size: size,
            thickness: thickness,
            sizeVariant: sizeVariant,
            isStatic: isStatic
          })
        })
      })
    });
  }

  // Overlay spinner
  if (children) {
    return /*#__PURE__*/_jsxs(SpinnerContainer, {
      inline: inline,
      "aria-live": "assertive",
      overlay: true,
      ...selectProps(rest),
      children: [/*#__PURE__*/_jsx(SpinnerContent, {
        label: label,
        labelPosition: labelPosition,
        overlay: true,
        size: size,
        thickness: thickness,
        sizeVariant: sizeVariant,
        isStatic: isStatic
      }), /*#__PURE__*/_jsx(ContentOverlay, {}), /*#__PURE__*/_jsx(OpaqueContainer, {
        inert: "true",
        children: recursiveMap(children, c => {
          if (c) {
            return /*#__PURE__*/React.cloneElement(c, {
              tabIndex: '-1',
              'aria-hidden': 'true'
            });
          }
          return undefined;
        })
      })]
    });
  }

  // Standalone spinner
  return /*#__PURE__*/_jsx(SpinnerContainer, {
    ref: ref,
    ...selectProps(rest),
    children: /*#__PURE__*/_jsx(SpinnerContent, {
      label: label,
      labelPosition: labelPosition,
      size: size,
      thickness: thickness,
      sizeVariant: sizeVariant,
      isStatic: isStatic
    })
  });
});
Spinner.displayName = 'Spinner';
Spinner.propTypes = {
  ...selectedSystemPropTypes,
  tokens: getTokensPropType('Spinner'),
  /**
   * Content to be overlaid while the spinner is active. Can be text, any HTML element,
   * or any component.
   */
  children: PropTypes.node,
  /**
   * Enables body locking.
   */
  fullScreen: PropTypes.bool,
  /**
   * Set the inline prop to true if the Spinner should only cover its children; if
   * the Spinner should cover the full width of its parent regardless of the size of
   * its children, inline should be set to false
   */
  inline: PropTypes.bool,
  /**
   * Communicates a message to assistive technology while visible. This same message
   * will appear underneath the spinner when its `size` is `large`.
   */
  label: PropTypes.string.isRequired,
  /**
   * The size of the spinner
   */
  // size: PropTypes.oneOf(['large', 'small']),
  /**
   * Whether or not to render the spinner.
   */
  show: PropTypes.bool,
  /**
   * If true, it should render a static spinner
   */
  isStatic: PropTypes.bool,
  /**
   * Determine where the label of the spinner should be placed, left, right, bottom or top.
   */
  labelPosition: PropTypes.string
};
export default Spinner;